/**
 * The return channel: queue, batch, retry.
 *
 * The downstream half of the relay has replay. **The upstream half has none** (08 §4.5), so a
 * coverage gap loses board output outright unless the page holds it — and because `logger.py`
 * joins on the far end's `rx seq=` lines, a silent gap costs *data*, not convenience (REQ-REM-8).
 *
 * No DOM. `post` and {@link Timers} arrive as arguments, so the whole module tests against fakes
 * with no browser and no real waiting.
 *
 * The queue is in memory and dies with the tab, which is acceptable and worth saying why: **the
 * phone's own log is the record of record**. Records are persisted as they arrive (REQ-LOG-3), so
 * what the outbox carries is a live copy for the operator's screen and for the laptop's
 * confirmation rule. Losing it costs control, not data (06 §12.12).
 */
import type { Timers } from './timers';

export type Upstream = {
  /** One board line, verbatim, terminator already stripped (08 §4.1). */
  out(line: string): void;
  /** One page answer from the closed set of 08 §4.3. Never board bytes. */
  note(line: string): void;
};

export type OutboxStats = {
  queued: number;
  bytes: number;
  /** Board lines lost — to the bound, or to a body the server refused outright. */
  dropped: number;
  state: 'idle' | 'sending' | 'retrying' | 'stopped';
};

export type Outbox = Upstream & { stats(): OutboxStats; stop(): void };

/** 08 §2.3 caps a body at 64 KiB. 60 leaves room for the byte count being an estimate. */
const MAX_BATCH = 60 * 1024;
/** 08 §4.5. A floor written to be safe, not a measured one — 02 §13.6. */
const MAX_LINES = 2000;
const MAX_BYTES = 256 * 1024;
/** A coverage gap is minutes, not seconds (08 §4.5). */
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];

type Path = 'out' | 'note';
/** `gap` is set only on a gap marker, and is what lets consecutive drops coalesce into one note. */
type Item = { path: Path; text: string; gap?: number };

/**
 * Console text is ASCII, so `length + 1` (for the `\n`) is exact today and an **under**estimate if
 * that ever stops being true — which is the safe direction against a 64 KiB server limit we sit
 * 4 KiB under.
 */
const size = (text: string): number => text.length + 1;
const weigh = (items: Item[]): number => items.reduce((n, i) => n + size(i.text), 0);

export function createOutbox(d: {
  post(url: string, body: string): Promise<number>;
  urls: { out: string; note: string };
  timers: Timers;
  onStats(s: OutboxStats): void;
}): Outbox {
  const q: Item[] = [];
  /**
   * The batch currently being POSTed, held **out** of the queue until it succeeds. This is what
   * makes "one POST in flight" an invariant rather than an intention: nothing can jump ahead of a
   * batch that is being retried, so line order survives every failure path. Order is a
   * correctness property here, not a throughput one — `logger.py` joins on it.
   */
  let inflight: Item[] | null = null;
  let qBytes = 0;
  let flightBytes = 0;
  let dropped = 0;
  /**
   * Queued `out` items. The line bound counts these and not notes, because notes are never
   * dropped — counting something undroppable towards a bound whose only remedy is dropping would
   * make the loop unable to satisfy it.
   */
  let outCount = 0;
  let attempt = 0;
  let timer: number | null = null;
  let stopped = false;
  let state: OutboxStats['state'] = 'idle';

  const stats = (): OutboxStats => ({
    queued: q.length + (inflight?.length ?? 0),
    bytes: qBytes + flightBytes,
    dropped,
    state,
  });
  const report = () => d.onStats(stats());

  /**
   * Record `n` lost board lines **at the position they were lost from**, coalescing with a marker
   * already there.
   *
   * 02 §3.4 said to queue the `gap` note at the head on the first success after a drop. That is
   * one note out of place: a note already at the head — a `sent <id>` the laptop is waiting on —
   * was produced *before* the hole, and unshifting past it reports the gap earlier than it
   * happened. Marking in place is strictly more accurate and no more code, so the ordering
   * promise ("the laptop learns about the hole before it reads the lines that follow it") holds
   * exactly rather than approximately.
   */
  function markGap(at: number, n: number): void {
    dropped += n;
    const prev = at > 0 ? q[at - 1] : undefined;
    if (prev?.gap !== undefined) {
      qBytes -= size(prev.text);
      prev.gap += n;
      prev.text = `gap ${prev.gap}`;
      qBytes += size(prev.text);
      return;
    }
    const marker: Item = { path: 'note', text: `gap ${n}`, gap: n };
    q.splice(at, 0, marker);
    qBytes += size(marker.text);
  }

  /**
   * Drop the **oldest `out`** until the bound holds, never a note. 08 §4.5 says drop the oldest
   * and report `gap <n>` — and `gap` counts *board lines*, so a dropped note could not be
   * reported at all. Notes are also tiny, and they are what the laptop is waiting on.
   */
  function enforceBound(): void {
    for (;;) {
      const overBytes = qBytes + flightBytes > MAX_BYTES;
      if (outCount <= MAX_LINES && !overBytes) return;
      const i = q.findIndex((it) => it.path === 'out');
      if (i < 0) return; // only notes left, and notes are never dropped: accept the overshoot
      const gone = q[i];
      if (!gone) return;
      const before = qBytes;
      q.splice(i, 1);
      qBytes -= size(gone.text);
      outCount--;
      markGap(i, 1);
      // A marker costs a few bytes where a one-character line cost two, so on the very first drop
      // of a region the byte total can rise. Without this the loop could spin on a queue it has
      // no way to shrink.
      if (outCount <= MAX_LINES && qBytes >= before) return;
    }
  }

  function push(path: Path, text: string): void {
    if (stopped) return;
    q.push({ path, text });
    qBytes += size(text);
    if (path === 'out') outCount++;
    enforceBound();
    report();
    kick();
  }

  /** Longest run of same-path items from the head, up to one body's worth. Always ≥ 1 item. */
  function takeBatch(): Item[] | null {
    const head = q[0];
    if (!head) return null;
    let n = 0;
    let bytes = 0;
    while (n < q.length) {
      const it = q[n];
      if (!it || it.path !== head.path) break;
      const s = size(it.text);
      if (n > 0 && bytes + s > MAX_BATCH) break; // a lone over-size line still goes, and 413s
      bytes += s;
      n++;
    }
    const batch = q.splice(0, n);
    qBytes -= bytes;
    if (head.path === 'out') outCount -= batch.length;
    flightBytes = bytes;
    return batch;
  }

  function kick(): void {
    if (stopped || inflight || timer !== null) return;
    const batch = takeBatch();
    if (!batch) {
      if (state !== 'idle') {
        state = 'idle';
        report();
      }
      return;
    }
    inflight = batch;
    state = 'sending';
    report();
    void send();
  }

  async function send(): Promise<void> {
    const batch = inflight;
    if (!batch || stopped) return;
    const first = batch[0];
    if (!first) return;
    const url = first.path === 'out' ? d.urls.out : d.urls.note;
    const body = batch.map((i) => `${i.text}\n`).join('');

    let status: number;
    try {
      status = await d.post(url, body);
    } catch {
      return retry(); // network error: the phone moved out of coverage, which is the normal case
    }
    if (stopped) return; // stop() landed while the POST was in flight

    if (status >= 200 && status < 300) return succeed();
    if (status === 404) return halt('stopped'); // wrong base, or the server restarted (08 §2.4)
    if (status === 413) return split();
    if (status >= 500) return retry();
    // Any other 4xx is permanent — retrying cannot fix a 400 or a 405, and blocking the queue on
    // it forever would lose everything behind it too. 08 §2.3 does not name this case; relayd
    // only emits 204/404/405/413/400, so reaching here means the base points at something that
    // is not our relay. Drop the batch, count it, keep going, and let `gap` say so.
    return discard();
  }

  function succeed(): void {
    inflight = null;
    flightBytes = 0;
    attempt = 0;
    // No gap bookkeeping here: a drop marks itself in place, at the position it happened.
    state = 'idle';
    report();
    kick();
  }

  function retry(): void {
    state = 'retrying';
    const ms = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 30_000;
    attempt++;
    timer = d.timers.setTimeout(() => {
      timer = null;
      if (stopped || !inflight) return;
      state = 'sending';
      report();
      void send();
    }, ms);
    report();
  }

  /**
   * 413: halve the batch and send the first half immediately — this is a size problem, not
   * congestion, so it takes no backoff. Defensive only: commands are capped at 127 bytes and
   * board lines are short, so a batch should never reach the server's limit.
   */
  function split(): void {
    const batch = inflight;
    if (!batch) return;
    if (batch.length <= 1) {
      const lone = batch[0];
      inflight = null;
      flightBytes = 0;
      attempt = 0;
      // One line the server will not take at any size. A note cannot reach 64 KiB — the closed
      // set of 08 §4.3 is `sent`/`fail`/`gap` — so in practice this is always a board line.
      if (lone && lone.path === 'out') markGap(0, 1);
      report();
      kick();
      return;
    }
    const half = Math.ceil(batch.length / 2);
    const back = batch.slice(half);
    const keep = batch.slice(0, half);
    q.unshift(...back);
    qBytes += weigh(back);
    if (back[0]?.path === 'out') outCount += back.length;
    inflight = keep;
    flightBytes = weigh(keep);
    attempt = 0;
    void send();
  }

  function discard(): void {
    const batch = inflight;
    inflight = null;
    flightBytes = 0;
    attempt = 0;
    const lost = (batch ?? []).filter((it) => it.path === 'out').length;
    if (lost > 0) markGap(0, lost);
    report();
    kick();
  }

  function halt(to: OutboxStats['state']): void {
    stopped = true;
    state = to;
    if (timer !== null) {
      d.timers.clearTimeout(timer);
      timer = null;
    }
    // The queue is left as it is, so `stats().queued` truthfully says how much never arrived.
    report();
  }

  return {
    out: (line) => push('out', line),
    note: (line) => push('note', line),
    stats,
    stop: () => halt('stopped'),
  };
}
